import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { heroPool } from "@/lib/heroRotation";

// External links (e.g. Instagram) open in a new tab so visitors keep the store open.
function Cta({ url, testId, className, children }: any) {
  return /^https?:\/\//.test(url || "") ? <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testId} className={className}>{children}</a>
    : <Link to={url || "/"} data-testid={testId} className={className}>{children}</Link>;
}

function HeroText({ s }: { s: any }) {
  return (
    <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-12 pt-32 md:pt-48 pb-24 text-white">
      {s.eyebrow && <p className="eyebrow mb-6 rise" data-testid="hero-eyebrow">{s.eyebrow}</p>}
      <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight max-w-3xl rise rise-2" data-testid="hero-heading">{s.heading}</h1>
      {s.subheading && <p className="mt-6 max-w-lg text-sm md:text-base text-white/85 leading-relaxed rise rise-3" data-testid="hero-subheading">{s.subheading}</p>}
      <div className="mt-10 flex flex-wrap gap-4 rise rise-4">
        {s.cta_label && <Cta url={s.cta_url} testId="hero-cta-primary" className="bg-white text-neutral-900 px-9 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[var(--c-primary)] hover:text-white transition-colors">{s.cta_label}</Cta>}
        {s.cta2_label && <Cta url={s.cta2_url} testId="hero-cta-secondary" className="border border-white px-9 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-white hover:text-neutral-900 transition-colors">{s.cta2_label}</Cta>}
      </div>
    </div>
  );
}

function BannerText({ s }: { s: any }) {
  return (
    <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 py-14 text-white">
      {s.eyebrow && <p className="eyebrow mb-4 rise" data-testid="hero-eyebrow">{s.eyebrow}</p>}
      <h1 className="font-display font-light uppercase tracking-[0.12em] text-4xl sm:text-5xl lg:text-6xl max-w-4xl rise rise-2" data-testid="hero-heading">{s.heading}</h1>
      {s.subheading && <p className="mt-5 max-w-xl text-sm md:text-base text-white/90 rise rise-3" data-testid="hero-subheading">{s.subheading}</p>}
      {(s.cta_label || s.cta2_label) && <div className="mt-7 flex flex-wrap justify-center gap-3 rise rise-4">
        {s.cta_label && <Cta url={s.cta_url} testId="hero-cta-primary" className="bg-white text-neutral-900 px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-[var(--c-primary)] hover:text-white transition-colors">{s.cta_label}</Cta>}
        {s.cta2_label && <Cta url={s.cta2_url} testId="hero-cta-secondary" className="border border-white px-8 py-3.5 text-[11px] tracking-[0.25em] uppercase hover:bg-white hover:text-neutral-900 transition-colors">{s.cta2_label}</Cta>}
      </div>}
    </div>
  );
}

function Dots({ count, i, ms, paused, go, center = false }: { count: number; i: number; ms: number; paused: boolean; go: (n: number) => void; center?: boolean }) {
  return (
    <div className={`absolute z-20 bottom-8 inset-x-0 max-w-[1400px] mx-auto px-5 md:px-12 flex flex-wrap gap-1.5 sm:gap-2 ${center ? "justify-center" : ""}`} data-testid="hero-dots">
      {Array.from({ length: count }, (_, n) => (
        <button key={n} onClick={() => go(n)} aria-label={`Show image ${n + 1}`} aria-current={n === i}
          className={`relative h-[3px] overflow-hidden bg-white/40 hover:bg-white/70 transition-[width,background-color] duration-500 ${n === i ? "w-8 sm:w-12" : "w-3 sm:w-5"}`} data-testid={`hero-dot-${n}`}>
          {n === i && <span key={i} className="absolute inset-y-0 left-0 bg-white hero-progress" style={{ animationDuration: `${ms}ms`, animationPlayState: paused ? "paused" : "running" }} />}
        </button>
      ))}
    </div>
  );
}

export function HeroRotator({ b: block, banner = false }: any) {
  const slides = useMemo(() => {
    const f = new URLSearchParams(window.location.search).get("hero");
    return heroPool(block, new Date(), f != null && f !== "" ? Number(f) : null);
  }, [block]);
  const [i, setI] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const ms = Math.max(2, Number(block.interval) || 3) * 1000;
  const go = (n: number) => { setPrev(i); setI((n + slides.length) % slides.length); };
  useEffect(() => { setI(0); setPrev(null); }, [slides]);
  useEffect(() => {
    if (slides.length < 2 || paused) return;
    const t = setTimeout(() => go(i + 1), ms);
    return () => clearTimeout(t);
  }, [i, paused, slides, ms]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const n = slides[(i + 1) % slides.length]; if (n?.image) new Image().src = n.image; }, [i, slides]);
  const s = slides[i] || block;
  return (
    <section className={`relative overflow-hidden bg-neutral-900 ${banner ? "min-h-[380px] md:min-h-[46vh] flex" : "min-h-[78vh] md:min-h-[86vh]"}`} data-testid={banner ? "menu-hero" : "home-hero"} data-slide={s.name || ""} data-index={i}
      onPointerEnter={(e) => e.pointerType === "mouse" && setPaused(true)} onPointerLeave={(e) => e.pointerType === "mouse" && setPaused(false)}>
      {prev != null && slides[prev] && prev !== i && <img key={`p${prev}`} src={slides[prev].image} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <img key={`c${i}`} src={s.image} alt={s.heading || ""} className="absolute inset-0 w-full h-full object-cover hero-fade" data-testid="hero-image" />
      <div className={`absolute inset-0 ${banner ? "bg-black/35" : "bg-gradient-to-r from-black/55 via-black/30 to-black/10"}`} />
      {banner ? <div className="relative w-full"><BannerText key={`t${i}`} s={s} /></div> : <HeroText key={`t${i}`} s={s} />}
      {slides.length > 1 && <Dots count={slides.length} i={i} ms={ms} paused={paused} go={go} center={banner} />}
    </section>
  );
}
